import { useState, useEffect } from "react";
import api from "@/utils/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import UpgradeModal from "@/components/UpgradeModal";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Download, Edit, Trash2, FileText, Clock, Filter, TrendingUp, RotateCcw, HelpCircle, AlertCircle } from "lucide-react";
import { format, startOfMonth, endOfMonth, subMonths } from "date-fns";
import { de, enUS } from "date-fns/locale";

// Country-specific VAT rates (outside component to avoid recreation)
const VAT_RATES = {
  DE: [
    { value: 19, label: "19% (Regelsteuersatz)", description: "Für die meisten Leistungen", isDefault: true },
    { value: 7, label: "7% (Ermäßigter Steuersatz)", description: "Lebensmittel, Bücher, ÖPNV" },
    { value: 0, label: "0% (Reverse Charge / Kleinunternehmer)", description: "B2B EU-Ausland oder Kleinunternehmerregelung" }
  ],
  AT: [
    { value: 20, label: "20% (Normalsteuersatz)", description: "Für die meisten Leistungen", isDefault: true },
    { value: 13, label: "13% (Ermäßigter Steuersatz)", description: "Lebensmittel, Bücher, kulturelle Dienstleistungen" },
    { value: 10, label: "10% (Stark ermäßigter Steuersatz)", description: "Bücher, Zeitungen, bestimmte Lebensmittel" },
    { value: 0, label: "0% (Reverse Charge / Kleinunternehmer)", description: "B2B EU-Ausland oder Kleinunternehmerregelung" }
  ],
  CH: [
    { value: 8.1, label: "8,1% (Normalsatz)", description: "Für die meisten Leistungen", isDefault: true },
    { value: 3.8, label: "3,8% (Beherbergung)", description: "Hotellerie" },
    { value: 2.6, label: "2,6% (Reduzierter Satz)", description: "Lebensmittel, Bücher, Medikamente" },
    { value: 0, label: "0% (Steuerfrei / Kleinunternehmer)", description: "Export oder Kleinunternehmer" }
  ]
};

const getVatRates = (country) => {
  return VAT_RATES[country] || VAT_RATES.DE;
};

const getDefaultVatRate = (country) => {
  const rates = getVatRates(country);
  const defaultRate = rates.find(r => r.isDefault);
  return defaultRate ? defaultRate.value : rates[0].value;
};

export default function Invoices() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const locale = i18n.language === "de" ? de : enUS;
  
  const [invoices, setInvoices] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState("");
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [autoCreateDialogOpen, setAutoCreateDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [selectedProjectForAuto, setSelectedProjectForAuto] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [taxHelpModalOpen, setTaxHelpModalOpen] = useState(false);
  const [showReverseChargeWarning, setShowReverseChargeWarning] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: "",
    project_id: "",
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: "",
    service_date: new Date().toISOString().split('T')[0],
    service_date_end: "",
    country: "DE",  // Will be updated by useEffect when user loads
    status: "draft",
    notes: "",
    items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: 19, total: 0 }]  // Will be updated by useEffect
  });

  useEffect(() => {
    fetchInvoices();
    fetchCustomers();
    fetchProjects();
    fetchTimeEntries();
  }, []);

  useEffect(() => {
    filterInvoices();
  }, [invoices, selectedMonth, statusFilter]);
  
  // Update formData when user loads (for correct country & VAT rate)
  useEffect(() => {
    if (user && user.country) {
      const defaultVatRate = getDefaultVatRate(user.country);
      setFormData(prev => {
        // Only update if country is different to avoid infinite loop
        if (prev.country !== user.country) {
          console.log('Updating formData country from', prev.country, 'to', user.country);
          return {
            ...prev,
            country: user.country,
            items: prev.items.map(item => ({
              ...item,
              tax_rate: defaultVatRate
            }))
          };
        }
        return prev;
      });
    }
  }, [user]);

  const filterInvoices = () => {
    console.log('🟢 Filtering invoices...');
    console.log('🟢 Total invoices:', invoices.length);
    console.log('🟢 Selected month:', selectedMonth);
    console.log('🟢 Status filter:', statusFilter);
    
    let filtered = [...invoices];

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter((inv) => inv.status === statusFilter);
      console.log('🟢 After status filter:', filtered.length);
    }

    // Filter by month
    if (selectedMonth !== "all") {
      filtered = filtered.filter((inv) => {
        const invoiceMonth = inv.invoice_date.substring(0, 7); // Get YYYY-MM
        return invoiceMonth === selectedMonth;
      });
      console.log('🟢 After month filter:', filtered.length);
    }

    console.log('🟢 Final filtered invoices:', filtered.length);
    setFilteredInvoices(filtered);
  };

  const getMonthOptions = () => {
    const options = [{ value: "all", label: t('common.allMonths', 'Alle Monate') }];
    for (let i = 0; i < 12; i++) {
      const date = subMonths(new Date(), i);
      options.push({
        value: format(date, "yyyy-MM"),
        label: format(date, "MMMM yyyy", { locale })
      });
    }
    return options;
  };

  const calculateStats = () => {
    const total = filteredInvoices.reduce((sum, inv) => sum + inv.total, 0);
    const paid = filteredInvoices.filter(inv => inv.status === "paid").reduce((sum, inv) => sum + inv.total, 0);
    const draft = filteredInvoices.filter(inv => inv.status === "draft").reduce((sum, inv) => sum + inv.total, 0);
    const sent = filteredInvoices.filter(inv => inv.status === "sent").reduce((sum, inv) => sum + inv.total, 0);
    const open = sent; // sent but not paid = open
    
    return { total, paid, draft, open, count: filteredInvoices.length };
  };


  const fetchInvoices = async () => {
    try {
      console.log('🔵 Fetching invoices...');
      const response = await api.get('/invoices');
      console.log('🔵 Received invoices:', response.data.length);
      // Sort by created_at descending (newest first)
      const sorted = response.data.sort((a, b) => 
        new Date(b.created_at) - new Date(a.created_at)
      );
      setInvoices(sorted);
      console.log('🔵 Invoices state updated');
    } catch (error) {
      console.error("❌ Error fetching invoices:", error);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/projects');
      setProjects(response.data);
    } catch (error) {
      console.error("Error fetching projects:", error);
    }
  };

  const fetchTimeEntries = async () => {
    try {
      const response = await api.get('/time-entries');
      setTimeEntries(response.data);
    } catch (error) {
      console.error("Error fetching time entries:", error);
    }
  };

  const fetchCountries = async () => {
    // Not needed anymore, only DE
    return { DE: { name: "Deutschland", currency: "EUR" } };
  };


  const createInvoiceFromTimeEntries = async () => {
    // Check if user has access to automatic invoice creation (Basic, Pro, or Lifetime)
    const hasAutomaticInvoices = user && (
      user.plan === 'basic' ||
      user.plan === 'basic_monthly' || 
      user.plan === 'basic_yearly' ||
      user.plan === 'pro' || 
      user.plan === 'pro_monthly' || 
      user.plan === 'pro_yearly' ||
      user.plan === 'lifetime'
    );

    if (!hasAutomaticInvoices) {
      toast.error('Automatische Rechnungserstellung ist erst ab dem Basic Plan verfügbar. Bitte upgraden Sie Ihren Plan.');
      setUpgradeModalOpen(true);
      return;
    }

    if (!selectedProjectForAuto) {
      toast.error(t('invoices.selectProject', 'Please select a project'));
      return;
    }

    const project = projects.find(p => p.id === selectedProjectForAuto);
    if (!project) {
      toast.error(t('common.projectNotFound', 'Projekt nicht gefunden'));
      return;
    }

    // Get time entries for this project that are NOT yet invoiced
    const projectTimeEntries = timeEntries.filter(
      entry => entry.project_id === selectedProjectForAuto && 
               entry.duration_minutes && 
               !entry.invoiced  // Only include open time entries
    );

    if (projectTimeEntries.length === 0) {
      toast.error('Keine offenen Zeiteinträge für dieses Projekt vorhanden. Alle Stunden wurden bereits abgerechnet.');
      return;
    }

    // Calculate total hours
    const totalMinutes = projectTimeEntries.reduce((sum, entry) => sum + entry.duration_minutes, 0);
    const totalHours = totalMinutes / 60;

    // Use current country from formData
    const currentCountry = formData.country;
    
    // Create invoice item with project hourly rate
    const invoiceItem = {
      description: `Arbeitszeit für ${project.name}`,
      quantity: parseFloat(totalHours.toFixed(2)),
      unit_price: project.hourly_rate || 0,
      tax_rate: getDefaultVatRate(currentCountry),
      total: parseFloat((totalHours * (project.hourly_rate || 0)).toFixed(2))
    };

    // Set form data
    setFormData({
      customer_id: project.customer_id,
      project_id: project.id,
      invoice_date: format(new Date(), "yyyy-MM-dd"),
      due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
      service_date: "",
      service_date_end: "",
      country: currentCountry,
      status: "draft",
      notes: `Abrechnung für ${projectTimeEntries.length} Zeiteinträge`,
      items: [invoiceItem]
    });

    setAutoCreateDialogOpen(false);
    setDialogOpen(true);
    setSelectedProjectForAuto(""); // Reset selection
    toast.success(`Rechnung aus ${totalHours.toFixed(2)} Stunden erstellt`);
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index][field] = value;
    
    // Recalculate total for this item
    if (field === "quantity" || field === "unit_price") {
      newItems[index].total = newItems[index].quantity * newItems[index].unit_price;
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    const defaultVatRate = getDefaultVatRate(formData.country);
    setFormData({
      ...formData,
      items: [...formData.items, { description: "", quantity: 1, unit_price: 0, tax_rate: defaultVatRate, total: 0 }]
    });
  };

  const removeItem = (index) => {
    const newItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: newItems });
  };

  // Check if Reverse Charge might apply
  const checkReverseCharge = () => {
    if (!formData.customer_id) return false;
    
    const customer = customers.find(c => c.id === formData.customer_id);
    if (!customer) return false;
    
    // Check if customer has VAT ID and is in different EU country
    const hasVatId = customer.vat_id && customer.vat_id.trim().length > 0;
    const isDifferentCountry = customer.country_code && customer.country_code !== user?.country;
    const isEUCountry = ['DE', 'AT', 'BE', 'BG', 'CY', 'CZ', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE', 'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK'].includes(customer.country_code);
    
    return hasVatId && isDifferentCountry && isEUCountry;
  };

  // Update reverse charge warning when customer changes
  useEffect(() => {
    setShowReverseChargeWarning(checkReverseCharge());
  }, [formData.customer_id, customers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation: Service date is required
    if (!formData.service_date) {
      toast.error(t('invoices.serviceDateRequired', 'Service date is required'));
      return;
    }
    
    try {
      const data = {
        ...formData,
        invoice_date: new Date(formData.invoice_date).toISOString(),
        due_date: new Date(formData.due_date).toISOString(),
        service_date: new Date(formData.service_date).toISOString(),
        service_date_end: formData.service_date_end ? new Date(formData.service_date_end).toISOString() : null,
      };

      if (editingInvoice) {
        console.log('🟡 Updating invoice:', editingInvoice.id);
        await api.put(`/invoices/${editingInvoice.id}`, data);
        toast.success(t("common.save"));
        
        // If project_id exists and time entries aren't linked yet, link them now
        if (formData.project_id) {
          try {
            console.log(`🔗 Ensuring time entries are linked to invoice ${editingInvoice.id}`);
            await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${editingInvoice.id}&invoice_status=${data.status}`);
          } catch (error) {
            console.error("Note: Time entries may already be linked or not exist");
          }
        }
      } else {
        console.log('🟡 Creating new invoice...');
        const response = await api.post(`/invoices`, data);
        console.log('🟡 Invoice created:', response.data.id);
        toast.success(t("common.create"));
        
        // Mark time entries as invoiced if this was created from time tracking
        if (formData.project_id) {
          try {
            console.log(`🔗 Linking time entries to invoice ${response.data.id} with status ${data.status}`);
            await api.post(`/time-entries/mark-invoiced?project_id=${formData.project_id}&invoice_id=${response.data.id}&invoice_status=${data.status}`);
            console.log('✅ Time entries marked as invoiced');
          } catch (error) {
            console.error("❌ Error marking time entries:", error);
            toast.warning("Rechnung erstellt, aber Zeiteinträge konnten nicht verknüpft werden");
          }
        }
      }

      setDialogOpen(false);
      setEditingInvoice(null);
      // Keep the current country from formData (which was set by useEffect)
      const currentCountry = formData.country;
      setFormData({
        customer_id: "",
        project_id: "",
        invoice_date: "",
        due_date: "",
        service_date: "",
        service_date_end: "",
        country: currentCountry,
        status: "draft",
        notes: "",
        items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
      });
      console.log('🟡 Calling fetchInvoices after save...');
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Error saving invoice");
      }
    }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm(t("common.delete") + "?")) return;
    try {
      await api.delete(`/invoices/${id}`);
      toast.success(t("common.delete"));
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error("Error deleting invoice");
      }
    }
  };

  const updateInvoiceStatus = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success(t('common.updated'));
      fetchInvoices();
    } catch (error) {
      if (error.response?.status === 403) {
        setUpgradeMessage(error.response.data.detail);
        setUpgradeModalOpen(true);
      } else {
        toast.error(t('common.error'));
      }
    }
  };

  const resetInvoiceStatus = async (invoiceId) => {
    if (!window.confirm(t('invoices.confirmResetDraft', 'Reset status to draft?'))) return;
    try {
      await api.put(`/invoices/${invoiceId}`, { status: "draft" });
      toast.success(t('invoices.resetToDraftSuccess', 'Status reset to draft'));
      fetchInvoices();
    } catch (error) {
      toast.error(t('invoices.resetError', 'Error resetting'));
    }
  };




  const downloadPDF = async (invoiceId) => {
    try {
      console.log('📄 Downloading PDF for invoice:', invoiceId);
      const response = await api.get(`/invoices/${invoiceId}/pdf`, {
        responseType: "blob"
      });
      console.log('📄 PDF received, size:', response.data.size);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `invoice_${invoiceId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(t("invoices.downloadPDF"));
    } catch (error) {
      console.error('📄 PDF download error:', error);
      toast.error(`PDF-Download fehlgeschlagen: ${error.response?.data?.detail || error.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: t("invoices.status.draft"), className: "bg-gray-100 text-gray-800" },
      sent: { label: t("invoices.status.sent"), className: "bg-blue-100 text-blue-800" },
      paid: { label: t("invoices.status.paid"), className: "bg-green-100 text-green-800" },
      cancelled: { label: t("invoices.status.cancelled"), className: "bg-red-100 text-red-800" },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const calculateSubtotal = () => {
    return formData.items.reduce((sum, item) => sum + item.total, 0);
  };

  const calculateTaxTotal = () => {
    return formData.items.reduce((sum, item) => sum + (item.total * item.tax_rate / 100), 0);
  };

  return (
    <div className="space-y-8" data-testid="invoices-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t("invoices.title")}</h1>
          <p className="text-lg text-gray-600">{t("invoices.subtitle")}</p>
        </div>
        <div className="flex space-x-2">
          <Dialog open={autoCreateDialogOpen} onOpenChange={setAutoCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="auto-create-invoice-button">
                <Clock className="h-4 w-4 mr-2" />
                Aus Zeiteinträgen
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-xl">✨ Rechnung automatisch erstellen</DialogTitle>
                <p className="text-sm text-gray-600 mt-2">
                  {t('invoices.createInvoiceHelp')}
                </p>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <div className="bg-blue-100 rounded-full p-2 shrink-0">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-blue-900 mb-1">Wie funktioniert's?</h3>
                      <ul className="text-sm text-blue-800 space-y-1">
                        <li>1. Wählen Sie ein Projekt mit erfassten Zeiteinträgen</li>
                        <li>2. Alle Stunden werden automatisch summiert</li>
                        <li>3. Der Betrag wird mit dem Stundensatz des Projekts berechnet</li>
                        <li>4. Die Rechnung wird als Entwurf erstellt</li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-base font-semibold mb-2 block">Projekt auswählen</Label>
                  <Select
                    value={selectedProjectForAuto}
                    onValueChange={setSelectedProjectForAuto}
                  >
                    <SelectTrigger data-testid="auto-project-select" className="h-12">
                      <SelectValue placeholder={t('invoices.selectProjectWithEntries', 'Select a project with time entries...')} />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.filter(p => {
                        // Only show projects with OPEN (not invoiced) time entries
                        const openEntries = timeEntries.filter(e => 
                          e.project_id === p.id && 
                          e.duration_minutes && 
                          !e.invoiced
                        );
                        return openEntries.length > 0;
                      }).map((project) => {
                        // Calculate totals only from OPEN time entries
                        const projectEntries = timeEntries.filter(
                          e => e.project_id === project.id && 
                               e.duration_minutes && 
                               !e.invoiced
                        );
                        const totalHours = projectEntries.reduce((sum, e) => sum + e.duration_minutes, 0) / 60;
                        const totalAmount = totalHours * project.hourly_rate;
                        const currency = user?.country === 'CH' ? 'CHF' : '€';
                        return (
                          <SelectItem key={project.id} value={project.id}>
                            <div className="flex flex-col py-1">
                              <span className="font-semibold">{project.name}</span>
                              <span className="text-xs text-gray-600">
                                {totalHours.toFixed(1)} offene Stunden × {project.hourly_rate}{currency}/h = {totalAmount.toFixed(2)}{currency}
                              </span>
                            </div>
                          </SelectItem>
                        );
                      })}
                      {projects.filter(p => {
                        const openEntries = timeEntries.filter(e => 
                          e.project_id === p.id && 
                          e.duration_minutes && 
                          !e.invoiced
                        );
                        return openEntries.length === 0;
                      }).length === projects.length && (
                        <div className="p-4 text-center text-gray-500 text-sm">
                          Keine Projekte mit offenen Zeiteinträgen gefunden
                        </div>
                      )}
                    </SelectContent>
                  </Select>
                  {selectedProjectForAuto && (
                    <p className="text-xs text-gray-600 mt-2">
                      ℹ️ Die Rechnung wird als Entwurf erstellt. Sie können sie vor dem Versand noch anpassen.
                    </p>
                  )}
                </div>

                <Button 
                  onClick={createInvoiceFromTimeEntries} 
                  className="w-full h-12 text-base"
                  data-testid="confirm-auto-create"
                  disabled={!selectedProjectForAuto}
                >
                  <FileText className="h-5 w-5 mr-2" />
                  Rechnung jetzt erstellen
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Manual create dialog */}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-invoice-button" onClick={() => {
                setEditingInvoice(null);
                // Use current country from formData (set by useEffect)
                const currentCountry = formData.country;
                setFormData({
                  customer_id: "",
                  project_id: "",
                  invoice_date: format(new Date(), "yyyy-MM-dd"),
                  due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
                  service_date: "",
                  service_date_end: "",
                  country: currentCountry,
                  status: "draft",
                  notes: "",
                  items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
                });
              }}>
                <Plus className="h-4 w-4 mr-2" />
                {t("invoices.newInvoice")}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl">
                {editingInvoice ? "📝 Rechnung bearbeiten" : "📄 Neue Rechnung erstellen"}
              </DialogTitle>
              <p className="text-sm text-gray-600 mt-2">
                {editingInvoice 
                  ? t('invoices.editDetails', 'Edit your invoice details.') 
                  : t('invoices.createInvoiceHelp', 'Create a new invoice for your client. All fields marked with * are required.')}
              </p>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="bg-gray-50 border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">📍 Rechnungsempfänger</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.customer")} *</Label>
                    <p className="text-xs text-gray-600 mb-2">Wählen Sie den Kunden für diese Rechnung</p>
                    <Select
                      value={formData.customer_id}
                      onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                      required
                    >
                      <SelectTrigger data-testid="invoice-customer-select" className="h-10">
                        <SelectValue placeholder={t('invoices.selectCustomer', 'Select client...')} />
                      </SelectTrigger>
                      <SelectContent>
                        {customers.map((customer) => (
                          <SelectItem key={customer.id} value={customer.id}>
                            {customer.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.project")} (optional)</Label>
                    <p className="text-xs text-gray-600 mb-2">Optional: Verknüpfen Sie mit einem Projekt</p>
                    <Select
                      value={formData.project_id}
                      onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                    >
                      <SelectTrigger data-testid="invoice-project-select" className="h-10">
                        <SelectValue placeholder="Kein Projekt" />
                      </SelectTrigger>
                      <SelectContent>
                        {projects.map((project) => (
                          <SelectItem key={project.id} value={project.id}>
                            {project.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-3">📅 Rechnungsdaten</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.invoiceDate")} *</Label>
                    <p className="text-xs text-gray-600 mb-2">Datum der Rechnungsstellung</p>
                    <Input
                      type="date"
                      value={formData.invoice_date}
                      onChange={(e) => setFormData({ ...formData, invoice_date: e.target.value })}
                      required
                      className="h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">{t("invoices.dueDate")} *</Label>
                    <p className="text-xs text-gray-600 mb-2">Zahlungsziel (üblicherweise 14-30 Tage)</p>
                    <Input
                      type="date"
                      value={formData.due_date}
                      onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                      required
                      className="h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-semibold">{t('invoices.serviceDate', 'Service Date')} *</Label>
                    <Input
                      id="service_date"
                      type="date"
                      value={formData.service_date}
                      onChange={(e) => setFormData({ ...formData, service_date: e.target.value })}
                      className="h-10"
                      max={formData.invoice_date || new Date().toISOString().split('T')[0]}
                      title={t('invoices.serviceDateNote', 'Service date cannot be after invoice date')}
                      required
                    />
                  </div>

                  <div>
                    <Label className="text-sm font-semibold">{t('invoices.serviceDateEnd', 'Service End Date')} ({t('common.optional', 'optional')})</Label>
                    <Input
                      id="service_date_end"
                      type="date"
                      value={formData.service_date_end || ''}
                      onChange={(e) => setFormData({ ...formData, service_date_end: e.target.value })}
                      className="h-10"
                      min={formData.service_date || ''}
                      max={formData.invoice_date || new Date().toISOString().split('T')[0]}
                      title={t('invoices.serviceDateEndNote', 'End date must be after start date')}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      {t('invoices.servicePeriodHelp', 'Leave empty for single date, fill for period')}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reverse Charge Warning */}
              {showReverseChargeWarning && (
                <div className="bg-orange-50 border border-orange-300 rounded-lg p-4 flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="font-semibold text-orange-900 mb-1">
                      ⚠️ Achtung: Reverse Charge könnte gelten
                    </p>
                    <p className="text-sm text-orange-800">
                      Ihr Kunde ist im EU-Ausland und hat eine UID-Nummer. Bei B2B-Geschäften 
                      innerhalb der EU gilt in der Regel das Reverse-Charge-Verfahren 
                      (Steuerschuldnerschaft des Leistungsempfängers). Sie sollten 0% Umsatzsteuer 
                      verwenden und den Hinweis "Steuerschuldnerschaft des Leistungsempfängers" 
                      in die Rechnungsnotizen aufnehmen.
                    </p>
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4 bg-gray-50">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <Label className="text-base font-semibold">{t("invoices.items")}</Label>
                    <p className="text-xs text-gray-600 mt-1">
                      Fügen Sie die Positionen Ihrer Rechnung hinzu (Leistungen, Produkte, Stunden, etc.)
                    </p>
                  </div>
                  <Button type="button" size="sm" onClick={addItem} className="shrink-0">
                    <Plus className="h-4 w-4 mr-1" />
                    Position hinzufügen
                  </Button>
                </div>

                {/* Header Row */}
                <div className="grid grid-cols-12 gap-3 mb-2 px-2">
                  <div className="col-span-4">
                    <span className="text-xs font-medium text-gray-700">Beschreibung *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">Menge *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">Einzelpreis ({formData.country === 'CH' ? 'CHF' : '€'}) *</span>
                  </div>
                  <div className="col-span-2">
                    <span className="text-xs font-medium text-gray-700">USt. (%) *</span>
                  </div>
                  <div className="col-span-1">
                    <span className="text-xs font-medium text-gray-700">Summe</span>
                  </div>
                  <div className="col-span-1">
                    {/* Action column */}
                  </div>
                </div>

                {/* Item Rows */}
                {formData.items.map((item, index) => (
                  <div key={index} className="bg-white border rounded-lg p-3 mb-3 hover:shadow-sm transition-shadow">
                    <div className="grid grid-cols-12 gap-3 items-center">
                      <div className="col-span-4">
                        <Input
                          placeholder="z.B. Webdesign, Beratung, Produkt..."
                          value={item.description}
                          onChange={(e) => handleItemChange(index, "description", e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="1"
                          value={item.quantity}
                          onChange={(e) => handleItemChange(index, "quantity", parseFloat(e.target.value) || 0)}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-2">
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          placeholder="0.00"
                          value={item.unit_price}
                          onChange={(e) => handleItemChange(index, "unit_price", parseFloat(e.target.value) || 0)}
                          className="h-10"
                        />
                      </div>
                      <div className="col-span-2 relative">
                        <div className="flex items-center gap-1">
                          <Select
                            value={item.tax_rate.toString()}
                            onValueChange={(value) => handleItemChange(index, "tax_rate", parseFloat(value))}
                          >
                            <SelectTrigger className="h-10">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {getVatRates(formData.country).map(rate => (
                                <SelectItem key={rate.value} value={rate.value.toString()}>
                                  <div className="flex flex-col">
                                    <span className="font-medium">{rate.label}</span>
                                    <span className="text-xs text-gray-500">{rate.description}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {index === 0 && (
                            <button
                              type="button"
                              onClick={() => setTaxHelpModalOpen(true)}
                              className="h-10 w-10 flex items-center justify-center hover:bg-gray-100 rounded-md transition-colors"
                              title="Hilfe zu Steuersätzen"
                            >
                              <HelpCircle className="h-5 w-5 text-blue-600" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="col-span-1">
                        <div className="h-10 flex items-center justify-end pr-2">
                          <span className="font-semibold text-gray-900">
                            {item.total.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="col-span-1 flex justify-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => removeItem(index)}
                          disabled={formData.items.length === 1}
                          className="hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                <div className="flex justify-between">
                  <span className="font-medium">{t("invoices.subtotal")}:</span>
                  <span>{calculateSubtotal().toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium">{t("invoices.taxTotal")}:</span>
                  <span>{calculateTaxTotal().toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'}</span>
                </div>
                <div className="flex justify-between text-lg font-bold border-t pt-2">
                  <span>{t("invoices.grandTotal")}:</span>
                  <span>{(calculateSubtotal() + calculateTaxTotal()).toFixed(2)} {formData.country === 'CH' ? 'CHF' : '€'}</span>
                </div>
              </div>

              <div className="bg-gray-50 border rounded-lg p-4">
                <Label className="text-sm font-semibold">💬 {t("invoices.notes")} (optional)</Label>
                <p className="text-xs text-gray-600 mb-2">
                  Zusätzliche Hinweise für den Kunden (z.B. Zahlungsbedingungen, Danksagung)
                </p>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder={t('invoices.notesExample', 'e.g. Thank you for your order! Payment by bank transfer please.')}
                />
              </div>

              <div className="flex gap-3">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    const currentCountry = formData.country;
                    setFormData({
                      customer_id: "",
                      project_id: "",
                      invoice_date: format(new Date(), "yyyy-MM-dd"),
                      due_date: format(new Date(Date.now() + 30*24*60*60*1000), "yyyy-MM-dd"),
                      service_date: "",
                      service_date_end: "",
                      country: currentCountry,
                      status: "draft",
                      notes: "",
                      items: [{ description: "", quantity: 1, unit_price: 0, tax_rate: getDefaultVatRate(currentCountry), total: 0 }]
                    });
                  }}
                  className="shrink-0"
                  title={t('invoices.resetForm', 'Reset form')}
                >
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button type="submit" className="w-full" data-testid="save-invoice-button">
                  {editingInvoice ? t("common.save") : t("common.create")}
                </Button>
              </div>
            </form>
          </DialogContent>
          </Dialog>

          {/* Tax Help Modal */}
          <Dialog open={taxHelpModalOpen} onOpenChange={setTaxHelpModalOpen}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <HelpCircle className="h-6 w-6 text-blue-600" />
                  Hilfe: Welcher Steuersatz gilt?
                </DialogTitle>
              </DialogHeader>
              
              <div className="space-y-6 text-sm">
                {formData.country === 'DE' && (
                  <>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">🇩🇪 Deutschland - Umsatzsteuersätze</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                          <p className="font-semibold mb-1">19% (Regelsteuersatz)</p>
                          <p className="text-gray-700">Gilt für die meisten Leistungen und Produkte</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="font-semibold mb-1">7% (Ermäßigter Steuersatz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Lebensmittel (außer Luxusartikel)</li>
                            <li>Bücher, Zeitungen, Zeitschriften</li>
                            <li>Personennahverkehr (ÖPNV)</li>
                            <li>Kulturelle Leistungen (Theater, Museen)</li>
                          </ul>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                          <p className="font-semibold mb-1">0% (Reverse Charge / Kleinunternehmer)</p>
                          <p className="text-gray-700 mb-2">Verwenden bei:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li><strong>Reverse Charge:</strong> B2B-Geschäfte ins EU-Ausland (Kunde hat UID-Nummer)</li>
                            <li><strong>Kleinunternehmerregelung:</strong> Umsatz {'<'} 22.000 € (Vorjahr) {'&'} {'<'} 50.000 € (laufendes Jahr)</li>
                          </ul>
                          <p className="text-sm text-orange-700 mt-2">
                            ⚠️ Bei Reverse Charge: Hinweis "Steuerschuldnerschaft des Leistungsempfängers gemäß § 13b UStG" in Rechnungsnotizen eintragen!
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {formData.country === 'AT' && (
                  <>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">🇦🇹 Österreich - Umsatzsteuersätze</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                          <p className="font-semibold mb-1">20% (Normalsteuersatz)</p>
                          <p className="text-gray-700">Gilt für die meisten Leistungen und Produkte</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="font-semibold mb-1">13% (Ermäßigter Steuersatz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Lebensmittel</li>
                            <li>Bücher, Zeitungen</li>
                            <li>Kulturelle Dienstleistungen</li>
                            <li>Wohnungsvermietung (bestimmte Fälle)</li>
                          </ul>
                        </div>
                        
                        <div className="bg-purple-50 border border-purple-200 p-3 rounded">
                          <p className="font-semibold mb-1">10% (Stark ermäßigter Steuersatz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Bücher (Verlagserzeugnisse)</li>
                            <li>Bestimmte Lebensmittel</li>
                            <li>Medikamente</li>
                          </ul>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                          <p className="font-semibold mb-1">0% (Reverse Charge / Kleinunternehmer)</p>
                          <p className="text-gray-700 mb-2">Verwenden bei:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li><strong>Reverse Charge:</strong> B2B-Geschäfte ins EU-Ausland (Kunde hat UID-Nummer)</li>
                            <li><strong>Kleinunternehmerregelung:</strong> Jahresumsatz {'<'} 35.000 €</li>
                          </ul>
                          <p className="text-sm text-orange-700 mt-2">
                            ⚠️ Bei Reverse Charge: Hinweis "Steuerschuldnerschaft des Leistungsempfängers gemäß Art. 19 UStG" in Rechnungsnotizen eintragen!
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                {formData.country === 'CH' && (
                  <>
                    <div>
                      <h3 className="font-semibold text-lg mb-2">🇨🇭 Schweiz - Mehrwertsteuersätze</h3>
                      <div className="space-y-3">
                        <div className="bg-green-50 border border-green-200 p-3 rounded">
                          <p className="font-semibold mb-1">8,1% (Normalsatz)</p>
                          <p className="text-gray-700">Gilt für die meisten Leistungen und Produkte</p>
                        </div>
                        
                        <div className="bg-blue-50 border border-blue-200 p-3 rounded">
                          <p className="font-semibold mb-1">3,8% (Sondersatz Beherbergung)</p>
                          <p className="text-gray-700">Gilt für Hotelleistungen und Beherbergung</p>
                        </div>
                        
                        <div className="bg-purple-50 border border-purple-200 p-3 rounded">
                          <p className="font-semibold mb-1">2,6% (Reduzierter Satz)</p>
                          <p className="text-gray-700 mb-2">Gilt für:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Lebensmittel (außer Getränke)</li>
                            <li>Bücher, Zeitungen, Zeitschriften</li>
                            <li>Medikamente</li>
                          </ul>
                        </div>
                        
                        <div className="bg-orange-50 border border-orange-200 p-3 rounded">
                          <p className="font-semibold mb-1">0% (Steuerfrei / Kleinunternehmer)</p>
                          <p className="text-gray-700 mb-2">Verwenden bei:</p>
                          <ul className="list-disc list-inside space-y-1 text-gray-600">
                            <li>Export von Leistungen ins Ausland</li>
                            <li>Kleinunternehmer (Jahresumsatz {'<'} CHF 100'000)</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                
                <div className="bg-gray-50 border border-gray-300 p-4 rounded">
                  <p className="font-semibold mb-2">💡 Wichtiger Hinweis:</p>
                  <p className="text-gray-700">
                    Diese Angaben dienen nur zur Orientierung und ersetzen keine professionelle Steuerberatung. 
                    Im Zweifelsfall konsultieren Sie bitte Ihren Steuerberater.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button onClick={() => setTaxHelpModalOpen(false)}>
                  Verstanden
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>


      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Gesamt</CardTitle>
            <FileText className="h-5 w-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">{calculateStats().count}</div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().total.toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-hover">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Entwurf</CardTitle>
            <FileText className="h-5 w-5 text-gray-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-gray-900">
              {filteredInvoices.filter(i => i.status === "draft").length}
            </div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().draft.toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-orange-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Offen</CardTitle>
            <TrendingUp className="h-5 w-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {filteredInvoices.filter(i => i.status === "sent").length}
            </div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().open.toFixed(2)} €</p>
          </CardContent>
        </Card>

        <Card className="card-hover border-green-200">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Bezahlt</CardTitle>
            <TrendingUp className="h-5 w-5 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {filteredInvoices.filter(i => i.status === "paid").length}
            </div>
            <p className="text-xs text-gray-600 mt-1">{calculateStats().paid.toFixed(2)} €</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Monat</Label>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger data-testid="month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Alle</SelectItem>
                  <SelectItem value="draft">Entwurf</SelectItem>
                  <SelectItem value="sent">Versendet (Offen)</SelectItem>
                  <SelectItem value="paid">Bezahlt</SelectItem>
                  <SelectItem value="cancelled">Storniert</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        {filteredInvoices.map((invoice, index) => (
          <Card key={invoice.id} className="card-hover" data-testid={`invoice-card-${invoice.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className="p-2 bg-emerald-50 rounded-lg">
                    <FileText className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{invoice.invoice_number}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{invoice.customer_name}</p>
                    {invoice.project_name && (
                      <p className="text-xs text-gray-500">{invoice.project_name}</p>
                    )}
                  </div>
                </div>
                {getStatusBadge(invoice.status)}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.invoiceDate")}</p>
                  <p className="font-medium">{format(new Date(invoice.invoice_date), "dd.MM.yyyy", { locale })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.dueDate")}</p>
                  <p className="font-medium">{format(new Date(invoice.due_date), "dd.MM.yyyy", { locale })}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.subtotal")}</p>
                  <p className="font-medium">{invoice.subtotal.toFixed(2)} {invoice.currency}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t("invoices.grandTotal")}</p>
                  <p className="font-semibold text-lg text-emerald-600">{invoice.total.toFixed(2)} {invoice.currency}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => downloadPDF(invoice.id)}
                  data-testid={`download-pdf-${invoice.id}`}
                >
                  <Download className="h-4 w-4 mr-1" />
                  PDF
                </Button>
                
                {/* Status update buttons */}
                {invoice.status === "draft" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateInvoiceStatus(invoice.id, "sent")}
                    className="border-blue-300 text-blue-700 hover:bg-blue-50"
                  >
                    Als versendet markieren
                  </Button>
                )}
                {invoice.status === "sent" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => updateInvoiceStatus(invoice.id, "paid")}
                    className="border-green-300 text-green-700 hover:bg-green-50"
                  >
                    Als bezahlt markieren
                  </Button>
                )}
                
                {/* Reset status button - show if not already draft */}
                {invoice.status !== "draft" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => resetInvoiceStatus(invoice.id)}
                    className="border-red-300 text-red-700 hover:bg-red-50"
                    title={t('invoices.resetToDraft', 'Reset to draft')}
                  >
                    <RotateCcw className="h-4 w-4" />
                  </Button>
                )}
                
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteInvoice(invoice.id)}
                  data-testid={`delete-invoice-${invoice.id}`}
                >
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredInvoices.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            {t("invoices.noInvoices", "Noch keine Rechnungen vorhanden. Erstellen Sie Ihre erste Rechnung!")}
          </CardContent>
        </Card>
      )}
      

    <UpgradeModal

      isOpen={upgradeModalOpen}

      onClose={() => setUpgradeModalOpen(false)}

      message={upgradeMessage}

      currentPlan={user?.plan}

    />

    </div>
  );
}